import { BankAccountHolderType } from '../bankaccounts/models/BankAccountHolderType';

export const mappingTypeValues = ['NORMAL', 'OVERPAYMENT', 'FEE', 'INTEREST', 'REFUND', 'MANUAL_REFUND', 'IGNORED'];

export type MappingType = (typeof mappingTypeValues)[number];

export type Mapping = {
  id: number;
  amount: number;
  fundSourceType: string;
  mappingType: MappingType;
  invoiceNumber?: number;
  invoiceMatchConfidence?: string;
  creditInvoiceNumber?: number;
  creditInvoiceMatchConfidence?: string;
  paymentNotificationEmailSent: boolean;
  mappedBy?: string;
  creationTime: string;
};

export const enum MatchConfidence {
  EXACT = 'EXACT',
  VERY_HIGH = 'VERY_HIGH',
  HIGH = 'HIGH',
  MANUAL = 'MANUAL',
}

export type BackofficeTrackingFragment = {
  internalComment?: string;
  merchantComment?: string;
};

export type BankAccountType = 'PG' | 'BG' | 'OTHER';

// Intended to be a flattened union of BankFilePaymentResponse (used for list view)
// and DetailedBankFilePaymentResponse (used for single view), with the latters non-optional fields
// being optional in the union.
export type BankFilePayment = {
  id: number;
  processedDate: string;
  fundSourceType: FundSourceType;
  amount: number;
  currencyCode: string;
  originReference: string;
  remainingUnmappedAmount: number;
  mappingError: string;
  paymentType: string;
  mappingStatus: string;
  endCustomerEmailSent: boolean;
  mappings: Mapping[];
  bankAccountNumber: string;
  bankAccountType: BankAccountType;
  possibleToRefund: boolean;
  merchantComment: string;
  toPayer: boolean;
  fullyMapped: boolean;
  doNotMap?: boolean;
  creationTime?: string;
  bookingDate?: string;
  transactionDate?: string;
  valueDate?: string;
  debtor?: Debtor;
  ultimateDebtor?: Debtor;
  creditInvoiceReference?: string;
  toMerchant?: MerchantMatch;
  backoffice?: BackofficeTrackingFragment;
  mappingErrorEmailSentConcerning?: MappingError;
  overpaymentEmailSent?: boolean;
  doNotMapReason?: DoNotMapReason;
  mappingErrorMessage?: string;
  viaAccountNumber?: string;
  viaAccountNumberType?: AccountNumberType;
  fromAccount?: BankAccountResponseV2;
  toAccount?: BankAccountResponseV2;
};

export type BankFilePaymentResponse = {
  id: number;
  processedDate: string;
  bookingDate?: string;
  valueDate?: string;
  fundSourceType: FundSourceType;
  amount: number;
  currencyCode: string;
  originReference: string;
  debtor?: Debtor;
  ultimateDebtor?: Debtor;
  remainingUnmappedAmount: number;
  mappingError: string;
  paymentType: string;
  mappingStatus: string;
  endCustomerEmailSent: boolean;
  mappings: Mapping[];
  bankAccountNumber: string;
  bankAccountType: BankAccountType;
  possibleToRefund: boolean;
  merchantComment: string;
  toPayer: boolean;
  fullyMapped: boolean;
};

export type DetailedBankFilePaymentResponse = {
  bankFilePaymentHeader: BankFilePaymentResponse;
  creationTime: string;
  bookingDate: string;
  transactionDate: string;
  fundSourceType: FundSourceType;
  toPayer: boolean;
  creditInvoiceReference: string;
  doNotMap: boolean;
  overpaymentEmailSent: boolean;
  mappings: Mapping[];
  fullyMapped: boolean;
  mappingErrorMessage: string;
  viaAccountNumber: string;
  viaAccountNumberType: AccountNumberType;
  doNotMapReason?: DoNotMapReason;
  toMerchant?: MerchantMatch;
  mappingErrorEmailSentConcerning?: MappingError;
  debtor?: Debtor;
  ultimateDebtor?: Debtor;
  backoffice?: BackofficeTrackingFragment;
};

export type BankAccountResponseV2 = {
  accountReference: string;
  currencyCode: string;
  countryCode: string;
  type: string;
  accountNumber: string;
  bic?: string;
  accountName?: string;
  accountHolderType?: BankAccountHolderType;
  accountHolderName?: string;
  viaAccountNumber?: string;
  viaAccountNumberType?: string;
  accountHolderCountry: string;
  createDate: string;
};

export type GetDetailedBankFilePaymentResponse = DetailedBankFilePaymentResponse & {
  fromAccount?: BankAccountResponseV2;
  toAccount?: BankAccountResponseV2;
};

export type MerchantMatch = {
  merchantId: string;
  matchConfidence: MatchConfidence;
};

export type DoNotMapReason = 'IGNORED' | 'REFUNDED' | 'CARD_SETTLEMENT' | 'SPLITTED' | 'OVERPAYMENT' | 'FEE';

export type FundSourceType = 'PAYMENT_TO_ACCOUNT' | 'CREDIT_INVOICE';

export type MappingError =
  | 'OVERPAYMENT'
  | 'OVERUSE'
  | 'INVOICE_NOT_FOUND'
  | 'WRONG_CURRENCY'
  | 'WRONG_INVOICE_TYPE'
  | 'NON_MATCHING_MERCHANT'
  | 'PART_OF_INVOICE_AGGREGATION'
  | 'OTHER'
  | 'DENIED_ORDER';

export type AccountNumberType = 'ACCOUNT_NUMBER' | 'IBAN' | 'PG' | 'BG';

export type Debtor = {
  name?: string;
  streetName?: string;
  zipCode?: string;
  regNumber?: string;
};

export const KNOWN_BICS: Record<string, string> = Object.freeze({
  SWEDSESS: 'Swedbank',
  NDEASESS: 'Nordea',
  DNBASESX: 'DNB',
  HANDSESS: 'Handelsbanken',
  ESSESESS: 'SEB',
});
